import { Box, Button, Card, Container, Group, SimpleGrid, Stack, Text, ThemeIcon, Title, useComputedColorScheme, useMantineTheme } from '@mantine/core'
import { BookOpen, Brain, Target } from 'lucide-react'
import { Link } from 'react-router-dom'
import { PublicLandingHeader } from '../../components/layout/PublicLandingHeader'

export function AboutUs() {
  const theme = useMantineTheme()
  const isDark = useComputedColorScheme('dark') === 'dark'

  const colors = {
    page: isDark ? theme.colors.dark[8] : 'var(--sat-bg)',
    surface: isDark ? theme.colors.dark[7] : 'var(--mantine-color-white)',
    mutedSurface: isDark ? theme.colors.dark[6] : 'var(--sat-surface-alt)',
    border: isDark ? theme.colors.dark[4] : 'var(--sat-border-soft)',
    text: isDark ? theme.white : 'var(--sat-text)',
    mutedText: isDark ? theme.colors.gray[3] : 'var(--sat-text-muted)',
    accent: 'var(--sat-red)',
    accentAlt: 'var(--sat-yellow)',
  }

  const pillars = [
    {
      icon: Brain,
      title: 'Smarter practice',
      description:
        'We turn SAT prep into a focused feedback loop with targeted drills, structured review, and progress insights that help students study with intention.',
    },
    {
      icon: Target,
      title: 'Clear score goals',
      description:
        'Every lesson, assignment, and recommendation is designed to move students toward concrete score outcomes instead of generic busywork.',
    },
    {
      icon: BookOpen,
      title: 'Tutor-friendly workflow',
      description:
        'SAT Tutor helps tutors organize students, track performance, and deliver personalized guidance without juggling multiple tools.',
    },
  ]

  return (
    <Box style={{ background: colors.page, minHeight: '100vh' }}>
      <PublicLandingHeader />
      <Container size="lg" py={{ base: 56, sm: 72 }}>
        <Stack gap={48}>
          <Box
            p={{ base: 'xl', sm: 40 }}
            style={{
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: 24,
            }}
          >
            <Stack gap="xl">
              <Stack gap="sm">
                <Text tt="uppercase" fw={700} fz="sm" c={colors.accent}>
                  About SAT Tutor
                </Text>
                <Title order={1} fz={{ base: 34, sm: 48 }} c={colors.text} maw={760}>
                  Built to make SAT prep more focused, measurable, and less overwhelming.
                </Title>
                <Text fz="lg" lh={1.75} c={colors.mutedText} maw={720}>
                  SAT Tutor combines guided practice, progress visibility, and tutor support so
                  students can spend less time guessing what to study and more time improving the
                  skills that matter.
                </Text>
              </Stack>

              <Group>
                <Button component={Link} to="/login" radius="xl" color="red">
                  Get started
                </Button>
                <Button component={Link} to="/contact" radius="xl" variant="light" color="red">
                  Contact us
                </Button>
              </Group>
            </Stack>
          </Box>

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
            {pillars.map(({ icon: Icon, title, description }) => (
              <Card
                key={title}
                padding="xl"
                radius="xl"
                style={{
                  background: colors.surface,
                  border: `1px solid ${colors.border}`,
                }}
              >
                <Stack gap="md">
                  <ThemeIcon size={48} radius="xl" variant="light" color="red">
                    <Icon size={22} />
                  </ThemeIcon>
                  <Title order={3} fz="xl" c={colors.text}>
                    {title}
                  </Title>
                  <Text lh={1.75} c={colors.mutedText}>
                    {description}
                  </Text>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Box
              p={{ base: 'xl', sm: 32 }}
              style={{
                background: colors.surface,
                border: `1px solid ${colors.border}`,
                borderRadius: 24,
              }}
            >
              <Stack gap="md">
                <Title order={2} fz={{ base: 26, sm: 30 }} c={colors.text}>
                  What we believe
                </Title>
                <Text lh={1.8} c={colors.mutedText}>
                  Great SAT prep should feel organized, encouraging, and data-informed. Students
                  deserve to know where they stand, what to do next, and why each study step
                  matters.
                </Text>
                <Text lh={1.8} c={colors.mutedText}>
                  Tutors deserve tools that save time, surface patterns quickly, and make it easier
                  to coach each student with confidence.
                </Text>
              </Stack>
            </Box>

            <Box
              p={{ base: 'xl', sm: 32 }}
              style={{
                background: colors.mutedSurface,
                border: `1px solid ${colors.border}`,
                borderRadius: 24,
              }}
            >
              <Stack gap="md">
                <Text tt="uppercase" fw={700} fz="sm" c={colors.accentAlt}>
                  Our mission
                </Text>
                <Title order={2} fz={{ base: 26, sm: 30 }} c={colors.text}>
                  Help more students reach ambitious score goals with less wasted effort.
                </Title>
                <Text lh={1.8} c={colors.mutedText}>
                  We focus on giving students the right practice at the right time while keeping
                  tutors aligned with the progress behind every assignment, drill, and review.
                </Text>
                <Text fw={600} c={colors.text}>
                  Better structure. Better feedback. Better results.
                </Text>
              </Stack>
            </Box>
          </SimpleGrid>
        </Stack>
      </Container>
    </Box>
  )
}
